import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranscribeAudio } from './useTranscribeAudio';

type DictationState = 'idle' | 'recording' | 'transcribing';

const SILENCE_THRESHOLD = 0.015;
const SILENCE_TIMEOUT_MS = 2000;
const LEVEL_POLL_MS = 80;

export function useDictation(
  organizationId: string | undefined,
  onTranscript: (text: string, autoStopped: boolean) => void,
) {
  const [state, setState] = useState<DictationState>('idle');
  const [error, setError] = useState<string | null>(null);
  const [audioLevel, setAudioLevel] = useState(0);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const levelIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const silenceStartRef = useRef<number | null>(null);
  const autoStoppedRef = useRef(false);
  const { mutateAsync: transcribe } = useTranscribeAudio(organizationId);

  const cleanupAudio = useCallback(() => {
    if (levelIntervalRef.current) {
      clearInterval(levelIntervalRef.current);
      levelIntervalRef.current = null;
    }
    if (audioContextRef.current) {
      audioContextRef.current.close().catch(() => {});
      audioContextRef.current = null;
    }
    analyserRef.current = null;
    silenceStartRef.current = null;
    setAudioLevel(0);
  }, []);

  const start = useCallback(async () => {
    setError(null);
    autoStoppedRef.current = false;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : 'audio/webm';
      const recorder = new MediaRecorder(stream, { mimeType });
      chunksRef.current = [];

      // Set up audio analysis for level monitoring + silence detection
      const audioContext = new AudioContext();
      const source = audioContext.createMediaStreamSource(stream);
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 256;
      source.connect(analyser);
      audioContextRef.current = audioContext;
      analyserRef.current = analyser;

      const dataArray = new Float32Array(analyser.fftSize);
      silenceStartRef.current = null;

      levelIntervalRef.current = setInterval(() => {
        if (!analyserRef.current) return;
        analyserRef.current.getFloatTimeDomainData(dataArray);

        // RMS level
        let sum = 0;
        for (let i = 0; i < dataArray.length; i++) {
          sum += dataArray[i] * dataArray[i];
        }
        const rms = Math.sqrt(sum / dataArray.length);
        setAudioLevel(Math.min(rms * 5, 1)); // scale up for visual feedback

        // Silence detection
        if (rms < SILENCE_THRESHOLD) {
          if (silenceStartRef.current === null) {
            silenceStartRef.current = Date.now();
          } else if (Date.now() - silenceStartRef.current >= SILENCE_TIMEOUT_MS) {
            // Auto-stop after sustained silence
            autoStoppedRef.current = true;
            if (mediaRecorderRef.current?.state === 'recording') {
              mediaRecorderRef.current.stop();
            }
          }
        } else {
          silenceStartRef.current = null;
        }
      }, LEVEL_POLL_MS);

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      recorder.onstop = async () => {
        const wasAutoStopped = autoStoppedRef.current;
        stream.getTracks().forEach((t) => t.stop());
        cleanupAudio();

        const blob = new Blob(chunksRef.current, { type: mimeType });
        if (blob.size === 0) {
          setState('idle');
          return;
        }

        setState('transcribing');
        try {
          const result = await transcribe(blob);
          if (result.transcript) {
            onTranscript(result.transcript, wasAutoStopped);
          }
        } catch (err: any) {
          setError(err?.message || 'Transcription failed');
        } finally {
          setState('idle');
        }
      };

      mediaRecorderRef.current = recorder;
      recorder.start();
      setState('recording');
    } catch (err: any) {
      cleanupAudio();
      if (err.name === 'NotAllowedError') {
        setError('Microphone access denied');
      } else {
        setError(err?.message || 'Failed to start recording');
      }
      setState('idle');
    }
  }, [transcribe, onTranscript, cleanupAudio]);

  const stop = useCallback(() => {
    if (mediaRecorderRef.current?.state === 'recording') {
      mediaRecorderRef.current.stop();
    }
  }, []);

  const toggle = useCallback(() => {
    if (state === 'recording') {
      stop();
    } else if (state === 'idle') {
      start();
    }
  }, [state, start, stop]);

  useEffect(() => {
    return () => {
      cleanupAudio();
    };
  }, [cleanupAudio]);

  return { state, error, audioLevel, toggle };
}
